"use client";

import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useBooking } from "@/contexts/BookingContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { CreditCard, Loader2, Minus, Plus, ShoppingBag, Wallet } from "lucide-react";
import { BookingStep, type BookingProduct } from "@/types/booking";
import SearchableCountrySelect from "@/components/ui/SearchableCountrySelect";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import {
  useInitiateProductPurchase,
  useLazyProductOrderSummary,
  useProductPurchasePreview,
} from "@/lib/apollo/hooks/booking";
import { useLazyValidatePhoneNumber } from "@/lib/apollo/hooks/booking-edge";
import { useLazySearchClient } from "@/lib/apollo/hooks/booking-edge";
import {
  formatPrice,
  getBusinessCurrency,
  getMinNgnCheckoutToastMessage,
  isBelowMinNgnCheckoutAmount,
  isStorefrontProductOutOfStock,
} from "@/lib/utils";
import { resolveProductOrderAmountPaid } from "@/lib/productOrderAmounts";
import {
  checkoutErrorMessage,
  retryOnStorefrontDisabled,
} from "@/lib/storefrontErrors";
import { PaymentProviderModal } from "@/components/booking/PaymentProviderModal";
import { StripeCheckoutModal } from "@/components/booking/StripeCheckoutModal";
import ProductOrderSuccess from "./ProductOrderSuccess";
import {
  getPaymentOptionConfig,
  getPaymentOptionsForCountry,
  type PaymentOption,
} from "@/lib/payment-providers";

const cleanPhone = (value: string) => value.replace(/[^0-9]/g, "");

declare global {
  interface Window {
    PaystackPop: any;
  }
}

const parseFullName = (fullName: string) => {
  const parts = fullName.trim().split(/\s+/);
  const firstName = parts[0] || "";
  const lastName = parts.slice(1).join(" ") || firstName;
  return { firstName, lastName };
};

const toPurchaseItems = (
  products: { id: string; quantity?: number }[],
) =>
  products.map((product) => ({
    productId: product.id,
    quantity: product.quantity || 1,
  }));

const wait = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

async function pollProductOrderSummary(
  getSummary: (options: {
    variables: { paymentReference?: string; invoiceNumber?: string };
  }) => Promise<any>,
  paymentReference?: string | null,
  invoiceNumber?: string | null,
) {
  if (!paymentReference && !invoiceNumber) return null;

  for (let attempt = 0; attempt < 8; attempt++) {
    try {
      const { data } = await getSummary({
        variables: {
          ...(paymentReference ? { paymentReference } : {}),
          ...(invoiceNumber ? { invoiceNumber } : {}),
        },
      });
      const summary = data?.productOrderSummary;
      const status = summary?.productOrder?.status || summary?.status;
      if (status && status !== "pending_payment") {
        return summary;
      }
    } catch {
      // Payment webhooks can lag; keep polling.
    }
    await wait(1500);
  }
  return null;
}

const normalizeSpaces = (value: string) => value.trim().replace(/\s+/g, " ");
const normalizeEmail = (value: string) => value.trim().toLowerCase();

const buyerEmailIsValid = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/** Preview lookup client. Sent only once phone or email can identify a buyer. */
const identifiablePreviewClient = ({
  fullName,
  email,
  phone,
  countryCode,
  callingCode,
}: {
  fullName: string;
  email: string;
  phone: string;
  countryCode: string;
  callingCode: string;
}) => {
  const cleanedPhone = cleanPhone(phone);
  const normalizedEmail = normalizeEmail(email);
  const emailValid = buyerEmailIsValid(normalizedEmail);
  if (cleanedPhone.length < 10 && !emailValid) return null;

  const { firstName, lastName } = parseFullName(fullName);
  return {
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    ...(emailValid ? { email: normalizedEmail } : {}),
    phone: cleanedPhone,
    countryCode,
    callingCode,
  };
};

const formatPhoneDisplay = (value: string) => {
  const cleanValue = value.replace(/[^0-9]/g, "");
  if (cleanValue.length <= 3) return cleanValue;
  if (cleanValue.length <= 6)
    return `${cleanValue.slice(0, 3)} ${cleanValue.slice(3)}`;
  if (cleanValue.length <= 10)
    return `${cleanValue.slice(0, 3)} ${cleanValue.slice(3, 6)} ${cleanValue.slice(6)}`;
  return `${cleanValue.slice(0, 4)} ${cleanValue.slice(4, 7)} ${cleanValue.slice(7, 11)}`;
};

export default function ProductCheckout() {
  useScrollToTop();

  const {
    selectedSalon,
    businessInfo,
    serviceClient,
    selectedProducts,
    modifyProductInCart,
    updateProductCartQuantity,
    clearProductCart,
    setProductOrder,
    addToast,
    handleNextStep,
  } = useBooking();
  const { user } = useAuth();

  const currency = getBusinessCurrency(businessInfo);
  const cartTotalAmount = selectedProducts.reduce(
    (sum, product) => sum + (product.retailPrice || 0) * (product.quantity || 1),
    0,
  );

  const defaultBuyer = useMemo(() => {
    return {
      firstName: serviceClient?.firstName || user?.firstName || "",
      lastName: serviceClient?.lastName || user?.lastName || "",
      email: serviceClient?.email || user?.email || "",
      countryCode:
        serviceClient?.countryCode || selectedSalon?.countryCode || "NG",
      callingCode:
        serviceClient?.callingCode || selectedSalon?.callingCode || "+234",
      phone: serviceClient?.phone || user?.phone || "",
    };
  }, [serviceClient, selectedSalon, user]);

  const [buyerFullName, setBuyerFullName] = useState("");
  const [buyer, setBuyer] = useState(defaultBuyer);
  const [loading, setLoading] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showStripeCheckout, setShowStripeCheckout] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(
    null,
  );
  const [paystackLoaded, setPaystackLoaded] = useState(false);
  const [pocketLoaded, setPocketLoaded] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [purchasedProducts, setPurchasedProducts] = useState<BookingProduct[]>(
    [],
  );
  const [purchasedBuyer, setPurchasedBuyer] = useState(defaultBuyer);
  const paymentCountryCode =
    selectedSalon?.countryCode || businessInfo?.countryCode;
  const availablePaymentOptions =
    getPaymentOptionsForCountry(paymentCountryCode);
  const [processingFeeAmount, setProcessingFeeAmount] = useState<number | null>(
    null,
  );
  const [successSummary, setSuccessSummary] = useState<{
    invoiceNumber: string;
    amount: number;
    message?: string | null;
    paymentReference?: string | null;
    products: BookingProduct[];
    buyer: {
      firstName: string;
      lastName: string;
      email: string;
      callingCode: string;
      phone: string;
    };
    subtotal: number;
    taxAmount: number;
    processingFee: number | null;
  } | null>(null);
  const [previewTotals, setPreviewTotals] = useState<{
    subtotal: number;
    productsTaxAmount: number;
    processingFeeAmount: number;
    totalAmountPayable: number;
    walletPayableAmount: number;
    clientWalletBalance: number;
    canPayWithClientWallet: boolean;
  } | null>(null);
  const [checkoutPaymentMethod, setCheckoutPaymentMethod] = useState<
    "online" | "client_wallet" | null
  >(null);

  const [initiateProductPurchase] = useInitiateProductPurchase();
  const [previewProductPurchase] = useProductPurchasePreview();
  const [getProductOrderSummary] = useLazyProductOrderSummary();
  const [validatePhoneNumberQuery] = useLazyValidatePhoneNumber();
  const [searchClientQuery] = useLazySearchClient();
  const [buyerPhoneValidation, setBuyerPhoneValidation] = useState<{
    isValid: boolean | null;
    loading: boolean;
  }>({ isValid: null, loading: false });
  const [buyerSearchLoading, setBuyerSearchLoading] = useState(false);
  const buyerLastSearchRef = useRef("");
  const pendingOrderRef = useRef<{
    invoiceNumber?: string | null;
    paymentReference?: string | null;
  }>({});
  const [buyerMatchedClient, setBuyerMatchedClient] = useState<{
    phone: string;
    fullName: string;
    email: string;
  } | null>(null);

  useEffect(() => {
    setBuyer(defaultBuyer);
    setBuyerFullName(
      [defaultBuyer.firstName, defaultBuyer.lastName].filter(Boolean).join(" "),
    );
  }, [defaultBuyer]);

  useEffect(() => {
    const cleaned = cleanPhone(buyer.phone || "");
    if (cleaned.length >= 4) {
      setBuyerPhoneValidation((prev) => ({ ...prev, loading: true }));
      const timeoutId = setTimeout(async () => {
        try {
          const { data } = await validatePhoneNumberQuery({
            countryCode: buyer.countryCode,
            phoneNumber: cleaned,
          });
          const valid = data?.validatePhoneNumber?.numberValid;
          setBuyerPhoneValidation({ isValid: valid ?? null, loading: false });
        } catch {
          setBuyerPhoneValidation((prev) => ({ ...prev, loading: false }));
        }
      }, 500);
      return () => clearTimeout(timeoutId);
    }
    setBuyerPhoneValidation({ isValid: null, loading: false });
  }, [buyer.phone, buyer.countryCode, validatePhoneNumberQuery]);

  useEffect(() => {
    if (!selectedSalon?.id) return;
    const cleaned = cleanPhone(buyer.phone || "");
    if (cleaned.length < 10) {
      buyerLastSearchRef.current = "";
      setBuyerSearchLoading(false);
      setBuyerMatchedClient(null);
      return;
    }

    const searchKey = `${buyer.countryCode}:${cleaned}`;
    if (buyerLastSearchRef.current === searchKey) return;

    const timeoutId = setTimeout(async () => {
      setBuyerSearchLoading(true);
      try {
        const { data } = await searchClientQuery({
          salonId: selectedSalon.id,
          phone: cleaned,
          countryCode: buyer.countryCode,
        });
        const existingClient = data?.clientSearch;
        if (existingClient) {
          const matchedFullName = normalizeSpaces(
            [existingClient.firstName, existingClient.lastName]
              .filter(Boolean)
              .join(" "),
          );
          setBuyer((prev) => ({
            ...prev,
            firstName: prev.firstName || existingClient.firstName || "",
            lastName: prev.lastName || existingClient.lastName || "",
            email: prev.email || existingClient.email || "",
            phone: cleaned,
          }));
          setBuyerFullName((prev) =>
            prev.trim()
              ? prev
              : [existingClient.firstName, existingClient.lastName]
                  .filter(Boolean)
                  .join(" "),
          );
          setBuyerMatchedClient({
            phone: cleaned,
            fullName: matchedFullName,
            email: normalizeEmail(existingClient.email || ""),
          });
          buyerLastSearchRef.current = searchKey;
        } else {
          setBuyerMatchedClient(null);
          buyerLastSearchRef.current = searchKey;
        }
      } catch {
        setBuyerMatchedClient(null);
      } finally {
        setBuyerSearchLoading(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [buyer.phone, buyer.countryCode, selectedSalon?.id, searchClientQuery]);

  useEffect(() => {
    if (!document.querySelector('script[src*="js.paystack.co"]')) {
      const paystackScript = document.createElement("script");
      paystackScript.src = "https://js.paystack.co/v2/inline.js";
      paystackScript.async = true;
      paystackScript.onload = () => setPaystackLoaded(true);
      document.body.appendChild(paystackScript);
    } else if (window.PaystackPop) {
      setPaystackLoaded(true);
    }

    if (!document.querySelector('script[src*="pocket-checkout-sdk"]')) {
      const pocketScript = document.createElement("script");
      pocketScript.src =
        "https://pocket-checkout-sdk.netlify.app/checkout-sdk.js";
      pocketScript.async = true;
      pocketScript.onload = () => setPocketLoaded(true);
      document.body.appendChild(pocketScript);
    } else if ((window as any).Pocket) {
      setPocketLoaded(true);
    }
  }, []);

  const buyerEditedFromMatched = Boolean(
    buyerMatchedClient &&
      (normalizeSpaces(buyerFullName) !== buyerMatchedClient.fullName ||
        normalizeEmail(buyer.email || "") !== buyerMatchedClient.email),
  );

  const hasOutOfStockItems = selectedProducts.some((product) =>
    isStorefrontProductOutOfStock(product),
  );
  const exceedsStock = selectedProducts.some(
    (product) => (product.quantity || 0) > (product.stockCount ?? 0),
  );
  const purchaseItems = toPurchaseItems(selectedProducts);
  const previewSubtotal = previewTotals?.subtotal ?? cartTotalAmount;
  const previewTaxAmount = previewTotals?.productsTaxAmount ?? 0;
  const goodsTotal = selectedSalon?.taxInclusivePricing
    ? previewSubtotal
    : previewSubtotal + previewTaxAmount;
  const canPayWithClientWallet = previewTotals?.canPayWithClientWallet === true;
  const walletPayableAmount = previewTotals?.walletPayableAmount ?? goodsTotal;
  const onlinePayableAmount = previewTotals?.totalAmountPayable ?? goodsTotal;
  const clientWalletBalance = previewTotals?.clientWalletBalance ?? 0;
  const productKey = selectedProducts
    .map((product) => `${product.id}:${product.quantity || 1}`)
    .join(",");
  const previewClient = identifiablePreviewClient({
    fullName: buyerFullName,
    email: buyer.email || "",
    phone: buyer.phone || "",
    countryCode: buyer.countryCode,
    callingCode: buyer.callingCode,
  });
  const previewClientKey = previewClient
    ? `${previewClient.countryCode}|${previewClient.callingCode}|${previewClient.phone}|${previewClient.email || ""}`
    : "";
  const previewClientRef = useRef(previewClient);
  previewClientRef.current = previewClient;
  const previewClientKeyRef = useRef(previewClientKey);
  const purchaseItemsRef = useRef(purchaseItems);
  purchaseItemsRef.current = purchaseItems;
  const previewPurchaseRef = useRef(previewProductPurchase);
  previewPurchaseRef.current = previewProductPurchase;

  useEffect(() => {
    if (!canPayWithClientWallet && checkoutPaymentMethod === "client_wallet") {
      setCheckoutPaymentMethod(null);
    }
  }, [canPayWithClientWallet, checkoutPaymentMethod]);

  useEffect(() => {
    const salonId = selectedSalon?.id;
    if (!salonId || !productKey) {
      setPreviewTotals(null);
      return;
    }

    const identityChanged = previewClientKeyRef.current !== previewClientKey;
    previewClientKeyRef.current = previewClientKey;
    if (identityChanged) {
      setPreviewTotals((current) =>
        current ? { ...current, canPayWithClientWallet: false } : current,
      );
    }

    const taxInclusive = selectedSalon?.taxInclusivePricing === true;
    let cancelled = false;
    const timeoutId = setTimeout(() => {
      const runPreview = async () => {
        try {
          const client = previewClientRef.current;
          const response = await retryOnStorefrontDisabled(
            () =>
              previewPurchaseRef.current({
                variables: {
                  input: {
                    salonId,
                    items: purchaseItemsRef.current,
                    channel: "card",
                    ...(client ? { client } : {}),
                  },
                },
              }),
            {
              isSuccess: (result) =>
                result.data?.productPurchasePreview?.status === 200,
              getMessage: (result) =>
                result.data?.productPurchasePreview?.errors?.[0]?.message ||
                result.errors?.[0]?.message,
            },
          );
          const payload = response.data?.productPurchasePreview;
          if (cancelled) return;
          if (payload?.status === 200 && payload.data) {
            const subtotal = payload.data.subtotal ?? cartTotalAmount;
            const productsTaxAmount = payload.data.productsTaxAmount ?? 0;
            setPreviewTotals({
              subtotal,
              productsTaxAmount,
              processingFeeAmount: payload.data.processingFeeAmount ?? 0,
              totalAmountPayable:
                payload.data.totalAmountPayable ?? cartTotalAmount,
              walletPayableAmount:
                payload.data.walletPayableAmount ??
                (taxInclusive ? subtotal : subtotal + productsTaxAmount),
              clientWalletBalance: payload.data.clientWalletBalance ?? 0,
              canPayWithClientWallet: client
                ? payload.data.canPayWithClientWallet === true
                : false,
            });
          }
        } catch {
          if (!cancelled) setPreviewTotals(null);
        }
      };

      void runPreview();
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [
    selectedSalon?.id,
    selectedSalon?.taxInclusivePricing,
    productKey,
    previewClientKey,
    cartTotalAmount,
  ]);

  const validate = () => {
    if (!selectedSalon?.id) {
      addToast({
        message: "Please select a location to continue.",
        variant: "error",
      });
      return false;
    }
    if (selectedProducts.length === 0) {
      addToast({
        message: "Please add a product to cart to continue.",
        variant: "error",
      });
      return false;
    }
    if (hasOutOfStockItems || exceedsStock) {
      addToast({
        message: "Remove out of stock items before checking out.",
        variant: "error",
      });
      return false;
    }
    if (isBelowMinNgnCheckoutAmount(goodsTotal, currency)) {
      addToast({
        message: getMinNgnCheckoutToastMessage(currency),
        variant: "error",
      });
      return false;
    }

    const { firstName, lastName } = parseFullName(buyerFullName);
    if (!firstName.trim()) {
      addToast({ message: "First name is required.", variant: "error" });
      return false;
    }
    if (!lastName.trim()) {
      addToast({ message: "Last name is required.", variant: "error" });
      return false;
    }
    if (!buyer.email?.trim()) {
      addToast({ message: "Email is required.", variant: "error" });
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyer.email.trim())) {
      addToast({ message: "Email is invalid.", variant: "error" });
      return false;
    }
    if (!cleanPhone(buyer.phone)) {
      addToast({ message: "Phone number is required.", variant: "error" });
      return false;
    }
    if (
      buyerPhoneValidation.isValid === false &&
      cleanPhone(buyer.phone).length >= 4
    ) {
      addToast({ message: "Phone number is invalid.", variant: "error" });
      return false;
    }
    return true;
  };

  const initiatePurchase = async ({
    paymentMethod,
    paymentProvider,
    channels,
  }: {
    paymentMethod: "online" | "client_wallet";
    paymentProvider?: string;
    channels?: string[];
  }) => {
    if (!selectedSalon?.id) return null;
    const buyerName = parseFullName(buyerFullName);
    const input = {
      salonId: selectedSalon.id,
      paymentMethod,
      ...(paymentMethod === "online" ? { paymentProvider, channels } : {}),
      client: {
        firstName: buyerName.firstName.trim(),
        lastName: buyerName.lastName.trim(),
        email: buyer.email.trim().toLowerCase(),
        phone: cleanPhone(buyer.phone),
        countryCode: buyer.countryCode,
        callingCode: buyer.callingCode,
      },
      items: purchaseItems,
    };

    const response = await retryOnStorefrontDisabled(
      () => initiateProductPurchase({ variables: { input } }),
      {
        isSuccess: (result) =>
          result.data?.initiateProductPurchase?.status === 200,
        getMessage: (result) =>
          result.data?.initiateProductPurchase?.errors?.[0]?.message ||
          result.errors?.[0]?.message,
      },
    );
    const payload = response.data?.initiateProductPurchase;
    if (payload?.status !== 200) {
      addToast({
        message: checkoutErrorMessage(
          payload?.errors?.[0]?.message || response.errors?.[0]?.message,
        ),
        variant: "error",
      });
      return null;
    }
    pendingOrderRef.current = {
      invoiceNumber: payload.data?.invoiceNumber,
      paymentReference: payload.data?.paymentReference,
    };
    return payload.data;
  };

  const handlePaymentClose = () => {
    addToast({ message: "Payment cancelled", variant: "error" });
    setLoading(false);
  };

  const completeSuccessfulPayment = async ({
    invoiceNumber,
    paymentReference,
    amountPaid,
    processingFee,
  }: {
    invoiceNumber?: string | null;
    paymentReference?: string | null;
    amountPaid?: number | null;
    processingFee?: number | null;
  }) => {
    const invoice =
      invoiceNumber || pendingOrderRef.current.invoiceNumber;
    const reference =
      paymentReference || pendingOrderRef.current.paymentReference;
    const summary = await pollProductOrderSummary(
      getProductOrderSummary,
      reference,
      invoice,
    );
    const orderNumber =
      summary?.invoiceNumber ||
      invoice ||
      "";
    const paid = resolveProductOrderAmountPaid({
      amountPaid: summary?.productOrder?.amountPaid ?? amountPaid,
      goodsTotal,
      chargedTotal: amountPaid,
    });
    const feeForReceipt =
      processingFee !== undefined ? processingFee : processingFeeAmount;
    const feeIncludedInPaid =
      feeForReceipt != null &&
      feeForReceipt > 0 &&
      paid + 0.009 >= goodsTotal + feeForReceipt;
    setSuccessSummary({
      invoiceNumber: orderNumber,
      amount: paid,
      message: summary?.message,
      products: selectedProducts.map((product) => ({ ...product })),
      buyer: { ...buyer },
      subtotal: previewSubtotal,
      taxAmount: previewTaxAmount,
      processingFee: feeIncludedInPaid ? feeForReceipt : null,
    });
    setPurchasedProducts(selectedProducts);
    setPurchasedBuyer(buyer);
    setProductOrder({
      invoiceNumber: orderNumber,
      paymentReference: paymentReference || summary?.productOrder?.id || null,
      totalPaid: paid,
      message: summary?.message,
      status: summary?.productOrder?.status || summary?.status,
    });
    clearProductCart();
    setShowSuccessDialog(true);
    setLoading(false);
  };

  const launchPaystackPayment = async (init: any, channels?: string[]) => {
    if (!window.PaystackPop) {
      addToast({
        message: "Payment system not ready. Please try again.",
        variant: "error",
      });
      setLoading(false);
      return;
    }
    const paystackKey = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY;
    if (!paystackKey) {
      addToast({
        message: "Payment system not configured. Please contact support.",
        variant: "error",
      });
      setLoading(false);
      return;
    }
    const paymentAuth = init.paymentAuthorization;
    if (!paymentAuth?.accessCode) {
      addToast({
        message: "Payment authorization failed. Please try again.",
        variant: "error",
      });
      setLoading(false);
      return;
    }
    try {
      const paystackPop = new window.PaystackPop();
      await paystackPop.checkout({
        key: paystackKey,
        email: buyer.email.trim(),
        amount: Math.round(
          (init.totalAmountPayable || cartTotalAmount) * currency.factor,
        ),
        currency: currency.code,
        accessCode: paymentAuth.accessCode,
        ...(channels && { channels }),
        onSuccess: () => {
          void completeSuccessfulPayment({
            invoiceNumber: init.invoiceNumber,
            paymentReference: init.paymentReference,
            amountPaid: init.totalAmountPayable,
          });
        },
        onCancel: () => handlePaymentClose(),
        onClose: () => handlePaymentClose(),
      });
    } catch {
      addToast({
        message: "Failed to initiate payment. Please try again.",
        variant: "error",
      });
      setLoading(false);
    }
  };

  const launchPocketPayment = async (init: any) => {
    const Pocket = (window as any).Pocket;
    if (!Pocket) {
      addToast({
        message: "Payment system not ready. Please try again.",
        variant: "error",
      });
      setLoading(false);
      return;
    }
    try {
      const payWithPocket = new Pocket({
        key: import.meta.env.VITE_POCKET_PUBLIC_KEY,
        business_id: import.meta.env.VITE_POCKET_BUSINESS_ID,
        wallet_id: import.meta.env.VITE_POCKET_WALLET_ID,
        amount: Math.round(
          (init.totalAmountPayable || cartTotalAmount) * currency.factor,
        ),
        reference: init.paymentReference,
        narration: "Product purchase",
        view: "popup",
        meta: {
          purpose: "product_purchase",
          salon_id: selectedSalon?.id,
          reference: init.paymentReference,
          processing_fee_amount: init.processingFeeAmount || 0,
          amount: init.subtotal ?? init.totalAmountPayable,
        },
        onSuccess: () => {
          void completeSuccessfulPayment({
            invoiceNumber: init.invoiceNumber,
            paymentReference: init.paymentReference,
            amountPaid: init.totalAmountPayable,
          });
        },
        onClose: () => handlePaymentClose(),
        onPending: () =>
          addToast({
            message:
              "Payment is being processed. Please wait for confirmation.",
            variant: "warning",
          }),
        onError: (err: any) => {
          addToast({
            message: err?.message || "Payment failed. Please try again.",
            variant: "error",
          });
          setLoading(false);
        },
      });
      payWithPocket.show();
    } catch {
      addToast({
        message: "Failed to initiate payment. Please try again.",
        variant: "error",
      });
      setLoading(false);
    }
  };

  const launchStripePayment = async (init: any) => {
    const paymentAuth = init.paymentAuthorization;
    if (!paymentAuth?.clientSecret) {
      addToast({
        message: "Payment authorization failed. Please try again.",
        variant: "error",
      });
      setLoading(false);
      return;
    }
    const stripePaid = resolveProductOrderAmountPaid({
      amountPaid: init.totalAmountPayable,
      goodsTotal,
      chargedTotal: init.totalAmountPayable,
    });
    const stripeFee =
      typeof init.processingFeeAmount === "number"
        ? init.processingFeeAmount
        : processingFeeAmount;
    const stripeFeeIncluded =
      stripeFee != null &&
      stripeFee > 0 &&
      stripePaid + 0.009 >= goodsTotal + stripeFee;
    setSuccessSummary({
      invoiceNumber: init.invoiceNumber || "",
      amount: stripePaid,
      paymentReference: init.paymentReference,
      products: selectedProducts.map((product) => ({ ...product })),
      buyer: { ...buyer },
      subtotal: previewSubtotal,
      taxAmount: previewTaxAmount,
      processingFee: stripeFeeIncluded ? stripeFee : null,
    });
    setStripeClientSecret(paymentAuth.clientSecret);
    setShowStripeCheckout(true);
  };

  const handleProviderSelect = (option: PaymentOption) => {
    setShowProviderModal(false);
    if (isBelowMinNgnCheckoutAmount(goodsTotal, currency)) {
      addToast({
        message: getMinNgnCheckoutToastMessage(currency),
        variant: "error",
      });
      return;
    }
    const config = getPaymentOptionConfig(option, paymentCountryCode);
    setLoading(true);
    const isProviderReady =
      config.provider === "paystack"
        ? paystackLoaded || window.PaystackPop
        : config.provider === "pocket"
          ? pocketLoaded || (window as any).Pocket
          : !!import.meta.env.VITE_STRIPE_PUBLIC_KEY;

    if (!isProviderReady) {
      addToast({
        message:
          config.provider === "stripe"
            ? "Payment system not configured. Please contact support."
            : "Payment provider is still loading. Please try again.",
        variant: "error",
      });
      setLoading(false);
      return;
    }

    initiatePurchase({
      paymentMethod: "online",
      paymentProvider: config.provider,
      channels: config.channels,
    })
      .then((init) => {
        if (!init) {
          setLoading(false);
          return;
        }
        const feeCandidate =
          typeof init.processingFeeAmount === "number"
            ? init.processingFeeAmount
            : Number(
                (init.totalAmountPayable ?? cartTotalAmount) - cartTotalAmount,
              );
        setProcessingFeeAmount(
          Number.isFinite(feeCandidate) && feeCandidate >= 0
            ? feeCandidate
            : null,
        );
        if (config.provider === "paystack") {
          launchPaystackPayment(init, config.channels);
          return;
        }
        if (config.provider === "stripe") {
          launchStripePayment(init);
          return;
        }
        launchPocketPayment(init);
      })
      .catch(() => {
        addToast({
          message: "Failed to initiate payment. Please try again.",
          variant: "error",
        });
        setLoading(false);
      });
  };

  const handleWalletPay = async () => {
    setLoading(true);
    try {
      const init = await initiatePurchase({ paymentMethod: "client_wallet" });
      if (!init) {
        setLoading(false);
        return;
      }
      setProcessingFeeAmount(0);
      await completeSuccessfulPayment({
        invoiceNumber: init.invoiceNumber,
        paymentReference: init.paymentReference,
        amountPaid: init.walletPayableAmount ?? walletPayableAmount,
        processingFee: 0,
      });
    } catch {
      addToast({
        message: "Failed to pay with wallet. Please try again.",
        variant: "error",
      });
      setLoading(false);
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;
    if (canPayWithClientWallet && checkoutPaymentMethod === null) return;
    if (checkoutPaymentMethod === "client_wallet") {
      void handleWalletPay();
      return;
    }
    setShowProviderModal(true);
  };

  const closeSuccess = () => {
    setShowSuccessDialog(false);
    setSuccessSummary(null);
    setPurchasedProducts([]);
    setPurchasedBuyer(defaultBuyer);
    setProductOrder(null);
    handleNextStep(BookingStep.INITIAL);
  };

  if (showSuccessDialog) {
    return (
      <ProductOrderSuccess
        invoiceNumber={successSummary?.invoiceNumber}
        products={
          successSummary?.products?.length
            ? successSummary.products
            : purchasedProducts
        }
        buyer={successSummary?.buyer || purchasedBuyer}
        businessInfo={businessInfo}
        selectedSalon={selectedSalon}
        currency={currency}
        subtotal={successSummary?.subtotal ?? previewSubtotal}
        taxAmount={successSummary?.taxAmount ?? previewTaxAmount}
        processingFee={successSummary?.processingFee ?? processingFeeAmount}
        totalPaid={successSummary?.amount ?? goodsTotal}
        onContinueShopping={closeSuccess}
      />
    );
  }

  if (!selectedSalon?.id) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8 lg:px-20">
        <div className="bg-white rounded-xl border border-grey-100 p-6">
          <h2 className="text-lg font-semibold text-grey-900 mb-2">
            Select a location
          </h2>
          <p className="text-sm text-grey-600 mb-4">
            Please go back and select a location to purchase a product.
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleNextStep(BookingStep.INITIAL)}
          >
            Back
          </Button>
        </div>
      </div>
    );
  }

  if (selectedProducts.length === 0 && !showSuccessDialog) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8 lg:px-20">
        <div className="bg-white rounded-xl border border-grey-100 p-6">
          <h2 className="text-lg font-semibold text-grey-900 mb-2">
            Product checkout
          </h2>
          <p className="text-sm text-grey-600 mb-4">
            Please add a product to your cart to continue.
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleNextStep(BookingStep.INITIAL)}
          >
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 lg:px-20">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-grey-100 p-6">
          <h2 className="text-lg font-semibold text-grey-900 mb-4">
            Order summary
          </h2>
          <div className="space-y-4">
            {selectedProducts.map((product) => {
              const outOfStock = isStorefrontProductOutOfStock(product);
              const atStockLimit =
                typeof product.stockCount === "number" &&
                (product.quantity || 0) >= product.stockCount;
              return (
                <div
                  key={product.id}
                  className="flex items-start justify-between gap-3"
                >
                  <div className="flex gap-3 min-w-0">
                    <div className="h-12 w-12 rounded-md overflow-hidden bg-grey-50 border border-grey-100 flex items-center justify-center shrink-0">
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <ShoppingBag className="h-5 w-5 text-grey-400" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-grey-900">
                        {product.name}
                      </p>
                      {outOfStock ? (
                        <p className="text-xs text-red-600">Out of stock</p>
                      ) : (
                        <div className="mt-2 inline-flex items-center border border-grey-200 rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() =>
                              modifyProductInCart(product.id, "remove")
                            }
                            className="px-2 py-1 text-grey-700 hover:bg-grey-50"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="px-3 py-1 text-xs text-grey-900 border-x border-grey-200">
                            {product.quantity}
                          </span>
                          <button
                            type="button"
                            disabled={atStockLimit}
                            onClick={() =>
                              updateProductCartQuantity(
                                product.id,
                                (product.quantity || 1) + 1,
                              )
                            }
                            className="px-2 py-1 text-grey-700 hover:bg-grey-50 disabled:opacity-40"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-grey-900 whitespace-nowrap">
                    {formatPrice(
                      (product.retailPrice || 0) * (product.quantity || 1),
                      currency,
                    )}
                  </p>
                </div>
              );
            })}
          </div>
          <div className="border-t border-grey-100 pt-4 mt-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-grey-600">Subtotal</span>
              <span className="font-semibold text-grey-900">
                {formatPrice(previewSubtotal, currency)}
              </span>
            </div>
            {previewTaxAmount > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-grey-600">Tax</span>
                <span className="font-semibold text-grey-900">
                  {formatPrice(previewTaxAmount, currency)}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-grey-600">Total (full payment)</span>
              <span className="font-semibold text-grey-900">
                {formatPrice(goodsTotal, currency)}
              </span>
            </div>
            <p className="text-xs text-grey-500">
              {canPayWithClientWallet && checkoutPaymentMethod === "client_wallet"
                ? "No processing fee"
                : "+ processing fee (based on payment method)"}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-grey-100 p-6">
          <h2 className="text-lg font-semibold text-grey-900 mb-4">
            Your details
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-grey-700">
                Phone number *
              </label>
              <div className="flex flex-col sm:flex-row gap-2 min-w-0">
                <SearchableCountrySelect
                  value={buyer.countryCode}
                  onChange={(countryCode, callingCode) =>
                    setBuyer((prev) => ({ ...prev, countryCode, callingCode }))
                  }
                  className="w-full sm:w-[160px] shrink-0 px-3 py-3 justify-between"
                />
                <input
                  type="tel"
                  value={formatPhoneDisplay(buyer.phone)}
                  onChange={(e) =>
                    setBuyer((prev) => ({
                      ...prev,
                      phone: e.target.value.replace(/[^0-9]/g, ""),
                    }))
                  }
                  className={`w-full sm:flex-1 min-w-0 border rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-green-300 ${
                    buyerPhoneValidation.isValid === true
                      ? "border-green-500"
                      : buyerPhoneValidation.isValid === false &&
                          cleanPhone(buyer.phone).length >= 4
                        ? "border-red-500"
                        : "border-grey-200"
                  }`}
                  placeholder="801 234 5678"
                  required
                />
              </div>
              {buyerSearchLoading && (
                <div className="flex items-center gap-2 mt-2">
                  <Loader2 className="h-4 w-4 animate-spin text-green-600" />
                  <span className="text-sm text-green-600">
                    Searching for existing account...
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-grey-700">
                Full Name *
              </label>
              <input
                value={buyerFullName}
                onChange={(e) => setBuyerFullName(e.target.value)}
                className="w-full border border-grey-200 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-green-300"
                placeholder="Jane Okon"
                required
                type="text"
              />
              {buyerEditedFromMatched && (
                <p className="text-amber-600 text-xs">
                  You’re editing details from an existing customer profile.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-grey-700">
                Email *
              </label>
              <input
                type="email"
                value={buyer.email || ""}
                onChange={(e) =>
                  setBuyer((prev) => ({ ...prev, email: e.target.value }))
                }
                className="w-full border border-grey-200 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-green-300"
                placeholder="janeokon@gmail.com"
                required
              />
            </div>

            {canPayWithClientWallet ? (
              <div className="space-y-3">
                <label className="flex items-start gap-3 p-4 border border-grey-100 rounded-lg cursor-pointer hover:bg-grey-50 transition-colors">
                  <input
                    type="radio"
                    name="product-payment"
                    value="client_wallet"
                    checked={checkoutPaymentMethod === "client_wallet"}
                    onChange={() => setCheckoutPaymentMethod("client_wallet")}
                    className="mt-1 w-4 h-4 accent-green-300 focus:ring-green-300"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Wallet className="h-4 w-4 text-grey-600" />
                      <span className="font-medium text-grey-900">
                        Pay with Wallet
                      </span>
                    </div>
                    <p className="text-sm text-grey-600">
                      Balance: {formatPrice(clientWalletBalance, currency)}
                    </p>
                    <p className="text-sm text-grey-600">
                      Pay {formatPrice(walletPayableAmount, currency)}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      Instant payment from wallet
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-4 border border-grey-100 rounded-lg cursor-pointer hover:bg-grey-50 transition-colors">
                  <input
                    type="radio"
                    name="product-payment"
                    value="online"
                    checked={checkoutPaymentMethod === "online"}
                    onChange={() => setCheckoutPaymentMethod("online")}
                    className="mt-1 w-4 h-4 accent-green-300 focus:ring-green-300"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <CreditCard className="h-4 w-4 text-grey-600" />
                      <span className="font-medium text-grey-900">
                        Pay Full Amount Online
                      </span>
                    </div>
                    <p className="text-sm text-grey-600">
                      Pay {formatPrice(onlinePayableAmount, currency)}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      {(previewTotals?.processingFeeAmount ?? 0) > 0
                        ? `Includes ${formatPrice(previewTotals?.processingFeeAmount, currency)} processing fee`
                        : "Instant confirmation"}
                    </p>
                  </div>
                </label>
              </div>
            ) : null}

            <Button
              type="submit"
              disabled={
                loading ||
                hasOutOfStockItems ||
                exceedsStock ||
                (canPayWithClientWallet && checkoutPaymentMethod === null)
              }
              className="w-full bg-green-300 hover:bg-green-400 text-white h-12"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : canPayWithClientWallet &&
                checkoutPaymentMethod === "client_wallet" ? (
                "Pay with Wallet"
              ) : canPayWithClientWallet && checkoutPaymentMethod === "online" ? (
                "Pay Full Amount Online"
              ) : (
                "Pay in full"
              )}
            </Button>
          </form>
        </div>
      </div>

      <PaymentProviderModal
        isOpen={showProviderModal}
        onClose={() => setShowProviderModal(false)}
        onSelectOption={handleProviderSelect}
        loading={loading}
        availableOptions={availablePaymentOptions}
      />
      <StripeCheckoutModal
        isOpen={showStripeCheckout}
        clientSecret={stripeClientSecret}
        onComplete={() => {
          setShowStripeCheckout(false);
          setStripeClientSecret(null);
          void completeSuccessfulPayment({
            invoiceNumber: successSummary?.invoiceNumber,
            paymentReference: successSummary?.paymentReference,
            amountPaid: successSummary?.amount,
          });
        }}
        onClose={() => {
          setShowStripeCheckout(false);
          setStripeClientSecret(null);
          handlePaymentClose();
        }}
      />
    </div>
  );
}
